import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class SocketService {

  private socket: Socket | null = null;
  private connectionSubject = new Subject<boolean>();
  public connection$ = this.connectionSubject.asObservable();

  constructor() { }

  /**
   * Connect socket for a specific logged-in user
   * Must be called ONCE after login
   */
  connect(): void {
    // Prevent duplicate connections
    if (this.socket) {
      if (this.socket.connected) {
        // console.log('[Socket] Already connected, skipping...');
        return;
      }
      // If socket exists but not connected, clean it up first
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user?.id) {
      // console.warn('[Socket] No user ID found, cannot connect');
      return;
    }


    this.socket = io(environment.socketURL, {
      auth: { userId: user.id },
      query: { userId: user.id }, // Also add as query parameter for compatibility
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      timeout: 20000
    });

    this.registerBaseEvents();
  }


  listen<T>(event: string): Observable<T> {
    return new Observable<T>(observer => {
      if (!this.socket) {
        // console.error(`[Socket] Cannot listen to '${event}': Socket not initialized`);
        observer.error('Socket not initialized');
        return;
      }

      if (!this.socket.connected) {
        // console.warn(`[Socket] Socket not connected when trying to listen to '${event}'`);
      }

      const handler = (data: T) => {
        // console.log(`[Socket] Event '${event}' received:`, data);
        observer.next(data);
      };

      // console.log(`[Socket] Registering listener for event: '${event}'`);
      this.socket.on(event, handler);

      return () => {
        // console.log(`[Socket] Removing listener for event: '${event}'`);
        this.socket?.off(event, handler);
      };
    });
  }

  /**
   * Emit event to backend (optional use)
   */
  emit(event: string, payload?: any): void {
    if (!this.socket) return;
    this.socket.emit(event, payload);
  }


  disconnect(): void {
    if (!this.socket) return;

    // Remove all listeners before disconnecting
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.connectionSubject.next(false);
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Global socket lifecycle events
   * Note: Notification events are handled by NotificationSocketService
   */
  private registerBaseEvents(): void {
    if (!this.socket) return;

    // Remove existing listeners to prevent duplicates
    this.socket.off('connect');
    this.socket.off('disconnect');
    this.socket.off('connect_error');

    this.socket.on('connect', () => {
      // console.log('[Socket] Connected:', this.socket?.id);
      // console.log('[Socket] Socket connected state:', this.socket?.connected);
      // console.log('[Socket] Socket transport:', this.socket?.io?.engine?.transport?.name);
      
      // Log all registered event listeners for debugging
      if (this.socket && (this.socket as any)._callbacks) {
        const events = Object.keys((this.socket as any)._callbacks || {});
        // console.log('[Socket] Registered event listeners:', events);
      }
      
      this.connectionSubject.next(true);
    });

    this.socket.on('disconnect', (reason: any) => {
      // console.warn('[Socket] Disconnected:', reason);
      this.connectionSubject.next(false);
    });

    this.socket.on('connect_error', (error: any) => {
      // console.error('[Socket] Connection error:', error.message);
    });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}